import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Search } from "lucide-react";
import { cn } from "@/lib/utils";

const fold = (s) =>
  (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const tokensMatch = (name, query) => {
  const q = fold(query);
  if (!q) return true;
  const n = fold(name);
  return q.split(" ").filter(Boolean).every((t) => n.includes(t));
};

/**
 * Input libre + sugerencias (platos propios y catálogo).
 * onPick(item | null) — null cuando solo se escribe texto nuevo.
 */
export const MealAutocomplete = ({
  value,
  onChange,
  onPick,
  items = [],
  placeholder = "Escribe o busca un plato…",
  testid = "meal-autocomplete",
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const filtered = useMemo(
    () => items.filter((it) => tokensMatch(it.name, value)).slice(0, 40),
    [items, value],
  );

  const personal = filtered.filter((it) => it.source === "personal");
  const catalog = filtered.filter((it) => it.source === "catalog");
  const exact = filtered.find((it) => fold(it.name) === fold(value));
  const showCreate = value.trim().length >= 2 && !exact;

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (item) => {
    onChange(item.name);
    onPick?.(item);
    setOpen(false);
  };

  const applyTyped = () => {
    onPick?.(null);
    setOpen(false);
  };

  const hint = (it) => {
    const parts = [];
    if (it.kcal != null) parts.push(`${it.kcal} kcal`);
    if (it.protein != null) parts.push(`P ${it.protein}g`);
    return parts.join(" · ");
  };

  return (
    <div ref={wrapRef} className="relative min-w-0" data-testid={testid}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
        <input
          type="text"
          disabled={disabled}
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          data-testid={`${testid}-input`}
          className="w-full rounded-2xl border border-stone-200 bg-white py-3 pl-10 pr-4 text-sm outline-none transition-colors placeholder:text-stone-400 hover:border-stone-300 focus:border-ink"
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            onChange(e.target.value);
            onPick?.(null);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setOpen(false);
            if (e.key === "Enter") {
              e.preventDefault();
              if (filtered[0]) pick(filtered[0]);
              else applyTyped();
            }
          }}
        />
      </div>
      {open && (filtered.length > 0 || showCreate) && (
        <div
          className="absolute z-[70] mt-1 max-h-64 w-full overflow-y-auto rounded-2xl border border-stone-200 bg-white py-1 shadow-lg"
          data-testid={`${testid}-list`}
        >
          {showCreate && (
            <button
              type="button"
              onClick={applyTyped}
              className="flex w-full items-start gap-2 px-3 py-2.5 text-left text-sm hover:bg-food-soft/60"
              data-testid={`${testid}-create`}
            >
              <span className="mt-0.5 text-xs font-bold uppercase tracking-wide text-food-ink">Nuevo</span>
              <span className="min-w-0 flex-1 font-medium text-ink">Usar «{value.trim()}»</span>
            </button>
          )}
          {personal.length > 0 && (
            <div>
              <p className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wide text-stone-400">Tus platos</p>
              {personal.map((it) => (
                <button
                  key={it.id || `p-${it.name}`}
                  type="button"
                  onClick={() => pick(it)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-food-soft/60"
                  data-testid={`${testid}-personal-${fold(it.name).replace(/\s+/g, "-")}`}
                >
                  <Check className={cn("h-3.5 w-3.5 shrink-0", fold(value) === fold(it.name) ? "text-food" : "text-transparent")} />
                  <span className="min-w-0 flex-1 truncate font-medium text-ink">{it.name}</span>
                  <span className="shrink-0 text-[11px] text-stone-500">{hint(it)}</span>
                </button>
              ))}
            </div>
          )}
          {catalog.length > 0 && (
            <div>
              <p className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wide text-stone-400">Catálogo</p>
              {catalog.map((it) => (
                <button
                  key={it.id || `c-${it.name}`}
                  type="button"
                  onClick={() => pick(it)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-food-soft/60"
                  data-testid={`${testid}-catalog-${fold(it.name).replace(/\s+/g, "-")}`}
                >
                  <Check className={cn("h-3.5 w-3.5 shrink-0", fold(value) === fold(it.name) ? "text-food" : "text-transparent")} />
                  <span className="min-w-0 flex-1 truncate font-medium text-ink">{it.name}</span>
                  <span className="shrink-0 text-[11px] text-stone-500">{hint(it)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
